import React, { useState, useRef, useEffect } from 'react';
import { useApi } from '../../../hooks/useApi';
import { UtmItem } from '../../../api/analytics/dto/UtmsListResp.dto';
import styles from './UtmAutocomplete.module.css';

interface UtmAutocompleteProps {
  type:
    | 'utm_source'
    | 'utm_medium'
    | 'utm_campaign'
    | 'utm_term'
    | 'utm_content';
  onSelect: (utmValue: string) => void;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const UtmAutocomplete: React.FC<UtmAutocompleteProps> = ({
  type,
  onSelect,
  value,
  onChange,
  placeholder = 'Начните вводить...',
}) => {
  const api = useApi();
  const [suggestions, setSuggestions] = useState<UtmItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const searchUtms = async (searchText: string) => {
    if (searchText.length < 1) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    setLoading(true);
    try {
      const response = await api.analytics.getUtmsList({
        type,
        text: searchText,
        limit: 10,
      });

      if (response.success && response.body && response.body.utms) {
        setSuggestions(response.body.utms || []);
        setIsOpen(true);
      } else {
        setSuggestions([]);
        setIsOpen(false);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Ошибка при поиске UTM меток:', error);
      setSuggestions([]);
      setIsOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    searchUtms(newValue);
  };

  const handleSelect = (utm: UtmItem) => {
    onSelect(utm.value);
    onChange('');
    setSuggestions([]);
    setIsOpen(false);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU');
    } catch {
      return dateString;
    }
  };

  const getTypeLabel = (utmType: string) => {
    const labels = {
      utm_source: 'Источник',
      utm_medium: 'Медиум',
      utm_campaign: 'Кампания',
      utm_term: 'Термин',
      utm_content: 'Контент',
    };
    return labels[utmType as keyof typeof labels] || utmType;
  };

  return (
    <div className={styles.autocomplete}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        className={styles.input}
        autoComplete="off"
      />

      {loading && <div className={styles.loading}>Поиск...</div>}

      {isOpen && suggestions && suggestions.length > 0 && (
        <div ref={dropdownRef} className={styles.dropdown}>
          {suggestions.map((utm, index) => (
            <div
              key={index}
              className={styles.suggestion}
              onClick={() => handleSelect(utm)}
            >
              <div className={styles.utmValue}>{utm.value}</div>
              <div className={styles.utmInfo}>
                <span className={styles.count}>{utm.count} использований</span>
                <span className={styles.date}>
                  Последнее: {formatDate(utm.last_used_date)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {isOpen &&
        (!suggestions || suggestions.length === 0) &&
        !loading &&
        value.length >= 1 && (
          <div ref={dropdownRef} className={styles.dropdown}>
            <div className={styles.noResults}>
              {getTypeLabel(type)} не найден
            </div>
          </div>
        )}
    </div>
  );
};

export default UtmAutocomplete;
